import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Switch } from '@/app/components/ui/switch';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { useCreateNearMiss } from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { NearMissSeverity } from '@/lib/types';

const severityOptions: { value: NearMissSeverity; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'border-green-500 bg-green-50 text-green-700' },
  { value: 'medium', label: 'Medium', color: 'border-yellow-500 bg-yellow-50 text-yellow-700' },
  { value: 'high', label: 'High', color: 'border-orange-500 bg-orange-50 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'border-red-500 bg-red-50 text-red-700' },
];

export function CreateNearMissPage() {
  const navigate = useNavigate();
  const createMutation = useCreateNearMiss();
  const orgToday = useOrgToday();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(orgToday);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [severityPotential, setSeverityPotential] = useState<NearMissSeverity>('medium');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [immediateActions, setImmediateActions] = useState('');
  const [contributingFactors, setContributingFactors] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const report = await createMutation.mutateAsync({
        title,
        date,
        location_fk: selectedLocation?.id || null,
        location,
        description,
        severity_potential: severityPotential,
        anonymous: isAnonymous,
      });
      navigate(`/safety/near-misses/${report.id}`);
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/near-misses')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report Near-Miss</h1>
          <p className="text-gray-600">Document a near-miss incident for review</p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Incident Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the near-miss"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Location</Label>
                <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
              </div>
            </div>
            <div>
              <Label htmlFor="location">Specific Area / Details</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. East face, 3rd floor scaffolding"
              />
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe what happened, who was involved, and what could have happened..."
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Severity Potential</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {severityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`border-2 rounded-lg p-4 text-center font-medium transition-all ${
                    severityPotential === option.value
                      ? option.color
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  onClick={() => setSeverityPotential(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="immediate">Immediate Actions Taken</Label>
              <Textarea
                id="immediate"
                value={immediateActions}
                onChange={(e) => setImmediateActions(e.target.value)}
                rows={2}
                placeholder="What was done immediately after the incident..."
              />
            </div>
            <div>
              <Label htmlFor="factors">Contributing Factors</Label>
              <Textarea
                id="factors"
                value={contributingFactors}
                onChange={(e) => setContributingFactors(e.target.value)}
                rows={2}
                placeholder="What factors contributed to this near-miss..."
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="anonymous"
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
              />
              <Label htmlFor="anonymous">Submit anonymously</Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/near-misses')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </form>
    </div>
  );
}
